import { useContext, useEffect, useState } from "react";
import { AuSessionPath } from "@arrowsup/platform-dtos";
import { PlatformUiEnvToHostMap } from "../data-provider/platform-ui-env-to-host-map";
import { PlatformUiEnv } from "../platform-ui-envs.type";
import { usePlatformUiEnv } from "../env/use-platform-ui-env";
import { AccessTokenContext } from "./access-token-context";

/**
 * Exchange the access token for a backend session cookie. Pages the browser
 * loads straight from the backend (dashboard iframes, job queues) use the
 * cookie, so tokens never go in URLs.
 */
export const createBackendSession = async (
  env: PlatformUiEnv,
  getAccessToken: () => Promise<string>
): Promise<void> => {
  const resp = await fetch(PlatformUiEnvToHostMap[env] + AuSessionPath, {
    method: "POST",
    credentials: "include",
    headers: { Authorization: `Bearer ${await getAccessToken()}` },
  });

  if (!resp.ok) {
    throw new Error(`Unable to start backend session (${resp.status})`);
  }
};

export const endBackendSession = async (env: PlatformUiEnv): Promise<void> => {
  await fetch(PlatformUiEnvToHostMap[env] + AuSessionPath, {
    method: "DELETE",
    credentials: "include",
  });
};

export interface BackendSessionState {
  ready: boolean;
  error?: unknown;
}

/** Creates a backend session for the current user on mount. */
export const useBackendSession = (): BackendSessionState => {
  const env = usePlatformUiEnv();
  const getAccessToken = useContext(AccessTokenContext).getAccessToken;
  const [state, setState] = useState<BackendSessionState>({ ready: false });

  useEffect(() => {
    let cancelled = false;
    setState({ ready: false });

    const create = async () => {
      try {
        await createBackendSession(env, getAccessToken);

        if (!cancelled) {
          setState({ ready: true });
        }
      } catch (error) {
        if (!cancelled) {
          setState({ ready: false, error });
        }
      }
    };

    void create();

    return () => {
      cancelled = true;
    };
  }, [env, getAccessToken]);

  return state;
};
